import get from "lodash/get";
import isNil from "lodash/isNil";
import isPlainObject from "lodash/isPlainObject";

const isEmail = (errorMessage: string, value: string) => {
  // email regex taken as a result of this PR thread:
  // https://github.com/friendbuy/fbot/pull/65#discussion_r842035449
  const emailRegEx = RegExp(/^[^\s@]+@([^\s@.,]+\.)+[^\s@.,]{2,}$/);

  const isEmailInvalid = !emailRegEx.test(value);

  if (isEmailInvalid) {
    return errorMessage;
  }

  return null;
};

const isEmailList = (errorMessage: string, value: string[]) => {
  if (!value || value.length === 0) {
    return errorMessage;
  }

  for (const email of value) {
    const error = isEmail(errorMessage, email);

    if (error !== null) {
      return `${email} is not a valid email address`;
    }
  }

  return null;
};

// Attribution's createEmailShare schema caps recipients at 10 and refuses
// outright, so without this the advocate gets a bare send failure.
const MAX_EMAIL_RECIPIENTS = 10;

const isWithinRecipientLimit = (
  errorMessage: string | null,
  value: string | string[]
) => {
  const recipients = Array.isArray(value) ? value : String(value).split(",");

  if (recipients.length <= MAX_EMAIL_RECIPIENTS) {
    return null;
  }

  return (
    errorMessage ||
    `You can send to up to ${MAX_EMAIL_RECIPIENTS} friends at a time. Please remove one.`
  );
};

const isNotEmpty = (
  errorMessage: string,
  value: string | boolean | undefined | null | number
) => {
  if (!value && value !== 0) {
    return errorMessage;
  }

  return null;
};

// "At least one selected", for multi-select inputs (FBE-6645).
//
// Deliberately separate from isNotEmpty rather than teaching it about lists:
// isNotEmpty is attached to Name, Email, Message and Redeem Amount inputs across
// every live widget config, and an empty array is truthy — so it silently passes
// an empty multi-select. Widening it would change behaviour for every one of
// those fields at once, for no benefit to any of them.
//
// A multi-select stores a delimited string (see MULTI_VALUE_DELIMITER in
// components/Dropdown), so in practice this receives "" or "red, blue". It
// accepts an array too because ComponentBase.validateField splits some
// components' values before validating.
const isNotEmptyList = (
  // The builder writes null when a merchant clears the message; the
  // `errorMessage || fallback` below is what makes that safe.
  errorMessage: string | null,
  value: string | string[] | undefined | null
) => {
  const values = Array.isArray(value)
    ? value
    : String(value === undefined || value === null ? "" : value).split(",");

  const hasSelection = values.some((entry) => String(entry).trim() !== "");

  if (!hasSelection) {
    // The fallback is load-bearing: the widget builder lets a merchant clear a
    // validator's message, writing null, and returning that bare would make a
    // failing field indistinguishable from a passing one. isCheckboxChecked
    // guards the same way.
    return errorMessage || "Please select at least one option";
  }

  return null;
};

export function isString(value?: any): boolean {
  return typeof value === "string";
}

export function isArray(value?: any[]): boolean {
  if (!Array.isArray) {
    return Object.prototype.toString.call(value) === "[object Array]";
  }

  return Array.isArray(value);
}

export function isObject(value?: any): boolean {
  return isPlainObject(value);
}

export function isEmptyObject(obj: any): boolean {
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      return false;
    }
  }

  return true;
}

export const isNotSelfReferral = (
  errorMessage: string,
  value: string | string[],
  data?: { [index: string]: { [index: string]: string | number | string[] } }
) => {
  const customerEmailAddress = get(data, "customer.email");

  const validate = (email: any) => {
    if (
      typeof customerEmailAddress === "string" &&
      customerEmailAddress === email
    ) {
      return errorMessage;
    }

    return null;
  };

  if (typeof value === "string") {
    return validate(value);
  } else if (isArray(value)) {
    for (const email of value) {
      const error = validate(email);

      if (error !== null) {
        return errorMessage;
      }
    }
  }

  return null;
};

export const hasActiveShopifyReward = (
  errorMessage: string,
  value: string | string[],
  data?: { [index: string]: { [index: string]: string | number | string[] } }
) => {
  const shopifyActiveDiscountsCount = get(
    data,
    "shopifyActiveDiscounts.count",
    0
  );
  const shopifyActiveGiftCardsCount = get(
    data,
    "shopifyActiveGiftCards.count",
    0
  );

  if (shopifyActiveDiscountsCount > 0 || shopifyActiveGiftCardsCount > 0) {
    return errorMessage;
  }

  return null;
};

export const isMoreThanLedgerHead = (
  errorMessage: string,
  value: string | string[],
  data?: { [index: string]: { [index: string]: string | number | string[] } }
) => {
  const ledgerHead = Number(get(data, "ledgerHead.balance", 0));

  if (Number(value) > ledgerHead) {
    return errorMessage;
  }

  return null;
};

// Validates that if a mobile number is provided, subscribeSms must be 'yes'
export const hasMobileNumberWithoutSubscribeSms = (
  errorMessage: string,
  value: string | string[],
  data?: {
    [index: string]: { [index: string]: string | number | string[] } | string;
  }
) => {
  const mobileNumber = get(data, "mobileNumber");
  const subscribeSms = get(data, "subscribeSms", "no");

  if (mobileNumber && subscribeSms !== "yes") {
    // Return error message if provided, otherwise return a default message
    // This ensures validation still works even if error message is empty
    return errorMessage || " ";
  }

  return null;
};

export const hasSubscribeSmsWithoutMobileNumber = (
  errorMessage: string,
  value: string | string[],
  data?: {
    [index: string]: { [index: string]: string | number | string[] } | string;
  }
) => {
  const mobileNumber = get(data, "mobileNumber");
  const subscribeSms = get(data, "subscribeSms", "no");

  if (subscribeSms === "yes" && !mobileNumber) {
    // Return error message if provided, otherwise return a default message
    // This ensures validation still works even if error message is empty
    return errorMessage || " ";
  }

  return null;
};

const isMobileNumber = (errorMessage: string, value: string) => {
  const mobileNumberRegEx = RegExp(/^(\(?\+?[0-9]*\)?)?[0-9_\- \(\)]*$/);

  const isMobileNumberInvalid =
    !isNil(value) && (!mobileNumberRegEx.test(value) || value.length > 30);

  if (isMobileNumberInvalid) {
    return errorMessage || " ";
  }

  return null;
};

export const isCheckboxChecked = (errorMessage: string, value: string) => {
  if (value !== "yes") {
    return errorMessage || " ";
  }

  return null;
};

// ENH-344: Zip Code "known field". Empty is allowed (pair with isNotEmpty to
// require it); a non-empty value must look like a postal code we recognise.
//
// Three formats rather than one permissive pattern. The field started US-only,
// which meant a UK or Canadian customer could not get past the widget and
// nothing was captured for them at all. Accepting anything non-empty would fix
// that but stop catching a mistyped "7870", so each country keeps its own shape:
//
//   US  12345 or 12345-6789
//   UK  SW1A 1AA — the space is optional
//   CA  M5V 3L9 — the space is optional
//
// Case-insensitive, since nobody types their postcode in caps.
//
// India (6-digit PIN) is deliberately NOT supported. It was added and then
// reverted at Samantha's request — the approved scope is US/UK/CA only, so this
// is a product decision rather than an oversight. Two consequences to know
// before re-adding it: sandbox holds 641001 values captured through the widget
// while it was allowed, and CS cannot re-save those customers' zip; and one
// regex per country does not scale, so the thing to revisit is a permissive
// pattern plus country-aware validation, not a fourth entry here.
const POSTAL_CODE_FORMATS: readonly RegExp[] = [
  /^\d{5}(-\d{4})?$/,
  /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i,
  // GIR 0AA is a real UK postcode (Girobank) and the one outward code with no
  // digit, so the general UK pattern above cannot match it. Kept here as well
  // as in the retailer app: if only CS accepted it, the widget would reject a
  // value a rep had just been allowed to save.
  /^GIR ?0AA$/i,
  /^[A-Z]\d[A-Z] ?\d[A-Z]\d$/i,
];

export const isZipCode = (errorMessage: string, value: string) => {
  if (isNil(value) || value === "") {
    return null;
  }

  const trimmed = String(value).trim();

  return POSTAL_CODE_FORMATS.some((format) => format.test(trimmed))
    ? null
    : errorMessage || " ";
};

const isValidMonth = (month: unknown): boolean =>
  Number.isInteger(month) && (month as number) >= 1 && (month as number) <= 12;

const isValidYear = (year: unknown): boolean =>
  Number.isInteger(year) && (year as number) >= 1900;

// Days per month, 1-indexed. February is the 28-day case; a supplied leap year
// is handled separately below.
const DAYS_IN_MONTH = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const isLeapYear = (year: number): boolean =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

/**
 * Whether day/month form a real calendar date. The widget's Day select always
 * offers 1–31 regardless of the chosen month, so February 30 and April 31 are
 * selectable and have to be rejected here.
 *
 * February is allowed 29 days unless a year is supplied that isn't a leap year —
 * without a year, Feb 29 is a legitimate birthday.
 */
const isRealCalendarDate = (
  day: unknown,
  month: unknown,
  year?: unknown
): boolean => {
  if (!isValidMonth(month) || !Number.isInteger(day)) {
    return false;
  }

  const monthIndex = month as number;
  const dayNumber = day as number;

  let maxDay = DAYS_IN_MONTH[monthIndex];
  if (monthIndex === 2) {
    maxDay = !Number.isInteger(year) || isLeapYear(year as number) ? 29 : 28;
  }

  return dayNumber >= 1 && dayNumber <= maxDay;
};

const birthdayIsEmpty = (value: any): boolean =>
  !value || (value.day == null && value.month == null && value.year == null);

// ENH-344: Birthday "known field" — the value is a {day, month, year?}
// object. "Is A Complete Date": an empty birthday is allowed (optional), but a
// partially entered one must be a real calendar date. A year is optional here,
// but if one was supplied it still has to be valid — otherwise a nonsense year
// would pass silently.
export const isCompleteDate = (errorMessage: string, value: any) => {
  if (birthdayIsEmpty(value)) {
    return null;
  }

  const { day, month, year } = value || {};

  if (year != null && !isValidYear(year)) {
    return errorMessage || " ";
  }

  return isRealCalendarDate(day, month, year) ? null : errorMessage || " ";
};

// Same as isCompleteDate but also requires a year — used when the Birthday
// component's "Collect birth year" setting is "required".
export const isCompleteDateWithYear = (errorMessage: string, value: any) => {
  if (birthdayIsEmpty(value)) {
    return null;
  }

  const { day, month, year } = value || {};

  return isValidYear(year) && isRealCalendarDate(day, month, year)
    ? null
    : errorMessage || " ";
};

export const validationFunctions = {
  hasActiveShopifyReward,
  hasMobileNumberWithoutSubscribeSms,
  hasSubscribeSmsWithoutMobileNumber,
  isEmail,
  isEmailList,
  isMobileNumber,
  isMoreThanLedgerHead,
  isNotEmpty,
  isNotEmptyList,
  isWithinRecipientLimit,
  isNotSelfReferral,
  isCheckboxChecked,
  isZipCode,
  isCompleteDate,
  isCompleteDateWithYear,
};

export const validate = (
  validators: string[][],
  value: string | string[] | boolean,
  data?: { [index: string]: { [index: string]: string | number | string[] } }
) => {
  let error = null;
  for (const validator of validators) {
    const validatorFunction = validationFunctions[validator[0]];
    const errorMessage = validator[1];

    if (validatorFunction) {
      error = validatorFunction(errorMessage, value, data);
    }

    if (error) {
      return error;
    }
  }
};
